import type { CrudFilter, CrudSort } from '@refinedev/core'
import { stringifyTableParams } from '@refinedev/core'
import type { AuditEntity, AuditEvent } from '@sirius/contracts/admin'
import type { OperatorColumnFilter } from '~/pages/cabinets/cabinetStatus'
import {
  CABINET_ACTIVITY_RESOURCE,
  RECORD_HISTORY_RESOURCE,
} from '~/providers/accessControl'
import {
  PLATFORM_ACTIVITY_RESOURCE,
  RECORD_ACTIVITY_RESOURCE,
} from '~/providers/dataProvider'
import { tenantOrigin } from '~/tenancy/tenantHost'

/** A record whose history a show page lists. */
export type HistoryRecord = {
  entity: AuditEntity
  id: string
  /**
   * the record's tenant (a tenant's own id): its history is scoped to it.
   * null for a platform record (a game): its events have no tenant and are
   * staff-only, in the platform log.
   */
  tenantId: string | null
}

/**
 * Where a record's history is read from (`list`) and who may see it
 * (`access`, a `CanAccess` resource). A tenant's record: its tenant's log,
 * for platform admins and the tenant's owners/admins; a cabinet's also for a
 * technician (the API gives it cabinet events only). A platform record: the
 * platform log, for platform admins only (tenant users never get its events
 * from the API either).
 */
export function historyResources({ tenantId, entity }: HistoryRecord) {
  if (tenantId === null) {
    return {
      list: PLATFORM_ACTIVITY_RESOURCE,
      access: PLATFORM_ACTIVITY_RESOURCE,
    }
  }
  return {
    list: RECORD_ACTIVITY_RESOURCE,
    access:
      entity === 'cabinet'
        ? CABINET_ACTIVITY_RESOURCE
        : RECORD_HISTORY_RESOURCE,
  }
}

/** The events a show page lists; "View all" opens the rest. */
export const HISTORY_PAGE_SIZE = 10

/** The activity list's filter fields that narrow it to one record. */
export const ENTITY_FIELD = 'entity'
export const ENTITY_ID_FIELD = 'entityId'
/**
 * A user's history (staff only): the events of their account and of their
 * memberships, which are records of their own (`member`).
 */
export const USER_FIELD = 'userId'

/**
 * The filters that narrow the log to the record: its entity (`eq`, or `in`
 * as the list's Entity column takes it) and id, or a user's history.
 */
function recordFilters(
  { entity, id }: HistoryRecord,
  entityOperator: 'eq' | 'in',
): CrudFilter[] {
  if (entity === 'user') {
    return [{ field: USER_FIELD, operator: 'eq', value: id }]
  }
  return [
    {
      field: ENTITY_FIELD,
      operator: entityOperator,
      value: entityOperator === 'in' ? [entity] : entity,
    },
    { field: ENTITY_ID_FIELD, operator: 'eq', value: id },
  ]
}

export const HISTORY_SORTERS: CrudSort[] = [
  { field: 'createdAt', order: 'desc' },
]

/**
 * The history's filters: the record, in the record's tenant
 * (`recordActivity`); a platform record has none (`platformActivity`).
 */
export function historyFilters(record: HistoryRecord): CrudFilter[] {
  return [
    ...(record.tenantId === null
      ? []
      : [
          {
            field: 'tenantId',
            operator: 'eq' as const,
            value: record.tenantId,
          },
        ]),
    ...recordFilters(record, 'eq'),
  ]
}

/**
 * `/activity` narrowed to the record, newest first, in the URL format the
 * list reads (`syncWithLocation`); `/platform/activity` for a platform
 * record. The entity filter is `in` like the Entity column's, so its
 * combobox shows the type selected.
 */
export function recordActivityPath(record: HistoryRecord) {
  const query = stringifyTableParams({
    filters: recordFilters(record, 'in'),
    sorters: HISTORY_SORTERS,
  })
  return `${record.tenantId === null ? '/platform/activity' : '/activity'}?${query}`
}

export type ViewAllLink =
  { kind: 'route'; to: string } | { kind: 'url'; href: string }

/**
 * Where "View all" goes: `/activity` on this host for the host tenant's
 * records; another tenant's log is on that tenant's host (a tenant's platform
 * page), null when its slug is unknown. A platform record's is
 * `/platform/activity` on this host.
 */
export function viewAllLink(
  record: HistoryRecord,
  {
    hostTenantId,
    tenantSlug,
    baseDomain,
    location,
  }: {
    hostTenantId: string
    /** the slug of the record's tenant */
    tenantSlug: string | undefined
    baseDomain: string
    location: { protocol: string; port: string }
  },
): ViewAllLink | null {
  const path = recordActivityPath(record)
  if (record.tenantId === null || record.tenantId === hostTenantId) {
    return { kind: 'route', to: path }
  }
  if (tenantSlug === undefined) {
    return null
  }
  return {
    kind: 'url',
    href: `${tenantOrigin(tenantSlug, baseDomain, location)}${path}`,
  }
}

/** The record (or user) the activity list is narrowed to, if any. */
export function recordFilterValue(
  filters: readonly OperatorColumnFilter[],
): string | undefined {
  const value = filters.find(
    ({ id }) => id === ENTITY_ID_FIELD || id === USER_FIELD,
  )?.value
  return typeof value === 'string' ? value : undefined
}

/**
 * The filters without the record (and its entity type, which came with it),
 * or without a user's history.
 */
export function withoutRecordFilter(
  filters: readonly OperatorColumnFilter[],
): OperatorColumnFilter[] {
  return filters.filter(
    ({ id }) =>
      id !== ENTITY_ID_FIELD && id !== ENTITY_FIELD && id !== USER_FIELD,
  )
}

/**
 * The record's name among the rows (`entityLabel`). The rows of a record
 * filter are that record's events, so the name is there.
 */
export function recordLabelIn(
  events: readonly AuditEvent[],
  entityId: string,
): string | undefined {
  for (const event of events) {
    if (event.entityId === entityId && event.entityLabel !== null) {
      return event.entityLabel
    }
  }
  return undefined
}
